import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, FilePen, Gauge, ShieldCheck } from "lucide-react";
import { stashTailor, tailorUrl } from "@/lib/tailor";
import { ScoreBurst } from "@/components/ScoreBurst";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Common";
import { ResultScore, extractScore } from "@/components/ResultScore";
import { BestResume } from "@/components/BestResume";
import { ToolRunner } from "@/pages/AIStudio";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, tierRank } from "@/lib/api";
import { getHistory, getPassMin, recordScore, useAtsHistory, usePassMin } from "@/lib/atsScores";
import { findAts, loadTools } from "@/lib/tools";

export const needsPreCheck = (jobId) => { const s = getHistory(jobId).at(-1)?.score; return s == null || s < getPassMin(); };

export const PreApplyCheck = ({ job, open, onClose, onApply }) => {
  const { tier } = useAuth();
  const score = useAtsHistory(job.id).at(-1)?.score ?? null;
  const PASS_MIN = usePassMin();
  const [tool, setTool] = useState(null);
  const [state, setState] = useState("running");
  const [err, setErr] = useState(null);
  const [boost, setBoost] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setErr(null);
    setState("running");
    loadTools().then(async (d) => {
      if (cancelled) return;
      const ats = findAts(d?.tools);
      if (!ats || tierRank(ats.tier) > tierRank(d?.tier || tier)) return setState("unavailable");
      setTool(ats);
      if (getHistory(job.id).length) return setState("idle");
      try {
        const { data } = await api.post(`/ai/tools/${ats.id}`, { job_id: job.id, inputs: {} });
        if (cancelled) return;
        const s = extractScore(data, true);
        if (s != null) recordScore(job.id, s, ats.id); else setErr("Couldn't read a score from the check.");
      } catch (e) { if (!cancelled) setErr(errMsg(e)); }
      if (!cancelled) setState("idle");
    });
    return () => { cancelled = true; };
  }, [open, job.id, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const pass = score != null && score >= PASS_MIN;
  const go = () => { onClose(); onApply(); };
  return (
    <>
      <Dialog open={open && !boost} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="border-[#27272A] bg-[#141417] sm:max-w-md" data-testid="pre-apply-dialog">
          <DialogTitle className="flex items-center gap-2 font-display text-zinc-100"><ShieldCheck className="h-5 w-5 text-[#D4AF37]" />Pre-apply ATS check</DialogTitle>
          <DialogDescription className="break-words text-zinc-400">{job.title}{job.company && ` · ${job.company}`}</DialogDescription>
          {state === "running" ? (
            <div className="flex items-center gap-3 rounded-xl border border-[#27272A] p-5 text-sm text-zinc-300" data-testid="pre-apply-running"><Spinner className="text-[#D4AF37]" />Checking your resume against this job...</div>
          ) : state === "unavailable" ? (
            <p className="text-sm text-zinc-400" data-testid="pre-apply-unavailable">The ATS Pass-Rate check isn't available on your plan, so we couldn't score this job.</p>
          ) : (
            <div className="space-y-3">
              {score != null && <ScoreBurst jobId={job.id} className="block"><ResultScore score={score} /></ScoreBurst>}
              {err && <p className="text-sm text-red-400" data-testid="pre-apply-error">{err} <button onClick={() => setAttempt((a) => a + 1)} className="ml-1 text-[#F3C649] underline" data-testid="pre-apply-retry">Retry</button></p>}
              {score != null && !pass && <p className="text-sm text-zinc-400" data-testid="pre-apply-warning">Below {PASS_MIN}% — boost your resume first to improve your odds.</p>}
              {pass && <p className="text-sm text-emerald-400" data-testid="pre-apply-pass">Looking good — you're ready to apply.</p>}
              <BestResume jobId={job.id} />
              <Link to={tailorUrl(job.id)} onClick={() => { stashTailor({ jobId: job.id, job: { title: job.title, company: job.company }, ideas: [] }); onClose(); }} className="inline-flex items-center gap-1.5 text-xs font-medium text-[#F3C649] hover:underline" data-testid="pre-apply-edit-resume"><FilePen className="h-3.5 w-3.5" />Tailor my resume for this job</Link>
            </div>
          )}
          <div className="mt-2 flex flex-wrap justify-end gap-2">
            <Button variant="ghost" onClick={onClose} data-testid="pre-apply-cancel">Cancel</Button>
            {tool && state === "idle" && score != null && !pass && <Button variant="outline" onClick={() => setBoost(true)} className="border-[#D4AF37]/50 text-[#F3C649]" data-testid="pre-apply-boost"><Gauge className="h-4 w-4" />Boost score</Button>}
            <Button onClick={go} disabled={state === "running"} data-testid="pre-apply-continue"><ExternalLink className="h-4 w-4" />{pass ? "Apply now" : "Apply anyway"}</Button>
          </div>
        </DialogContent>
      </Dialog>
      {boost && tool && <ToolRunner tool={tool} initialJob={{ id: job.id, title: job.title, company: job.company }} onClose={() => setBoost(false)} />}
    </>
  );
};
